import { isBookingDependentPlan } from '../Price/bookingDependentPricing'
import {
  IBookingRoomPricingPlan,
  hasConfiguredPerPlanBookingRates,
  isBookingRatePendingCommissionPlaceholder,
} from './getNewIndependentBookingPlans'
import { IItemData } from '.'

export function isActiveBookingIntegrationRoom(
  room: { deleted?: unknown; status?: unknown } | null | undefined,
): boolean {
  if (!room) return false
  if (String(room.deleted) === '1') return false
  if (String(room.status) === 'deleted') return false
  return true
}

export function collectActiveBookingPlanIds(
  bookingObjects: IItemData[] | null | undefined,
): Set<string> {
  const ids = new Set<string>()
  for (const obj of bookingObjects || []) {
    for (const room of obj.rooms || []) {
      if (!isActiveBookingIntegrationRoom(room)) continue
      const pricing = room && room.pricing
      if (!Array.isArray(pricing)) continue
      for (const p of pricing as IBookingRoomPricingPlan[]) {
        if (!p || p.deleted) continue
        const bid = p.booking_id
        if (bid === undefined || bid === null || bid === '') continue
        ids.add(String(bid))
      }
    }
  }
  return ids
}

/** Usuwa wpisy `rates` planów oznaczonych jako `deleted` w integracji Booking. */
export function filterBookingRatesToActivePlans<T>(
  rates: Record<string, T> | null | undefined,
  bookingObjects: IItemData[] | null | undefined,
): Record<string, T> {
  if (!rates || typeof rates !== 'object') return {}
  const activeIds = collectActiveBookingPlanIds(bookingObjects)
  if (activeIds.size === 0) {
    return { ...rates }
  }
  const out: Record<string, T> = {}
  for (const [id, value] of Object.entries(rates)) {
    if (activeIds.has(id)) {
      out[id] = value
    }
  }
  return out
}

export function shouldShowBookingPlanOnPriceGrid(
  planBookingId: string | number | undefined | null,
  booking:
    | {
        active?: unknown
        rates?: Record<string, unknown>
      }
    | undefined,
): boolean {
  if (
    !booking ||
    planBookingId === undefined ||
    planBookingId === null ||
    planBookingId === ''
  ) {
    return false
  }

  const rates =
    booking.rates &&
    typeof booking.rates === 'object' &&
    !Array.isArray(booking.rates)
      ? booking.rates
      : {}
  const key = String(planBookingId)
  const globalActive = Boolean(Number(booking.active))
  const hasConfiguredPerPlanRate = hasConfiguredPerPlanBookingRates(booking)

  if (globalActive && !hasConfiguredPerPlanRate) return true

  // Brak wpisu — nowy plan (pkt 3): widoczny na wszystkich pokojach w trybie per-plan.
  if (!Object.prototype.hasOwnProperty.call(rates, key)) {
    return !globalActive && hasConfiguredPerPlanRate
  }

  // Placeholder `0` — nowy plan niezależny: widoczny, ceny z Booking.
  return true
}

export function filterBookingPricingForPriceGrid<
  T extends {
    booking_id?: string | number
    deleted?: unknown
    is_child?: number | string
    id_parent?: string | number
  }
>(
  pricing: T[] | null | undefined,
  booking:
    | {
        active?: unknown
        rates?: Record<string, unknown>
      }
    | undefined,
): T[] {
  if (!pricing || !Array.isArray(pricing)) return []

  const visibleParentIds = new Set<string>()
  for (const plan of pricing) {
    if (!plan || plan.deleted) continue
    if (Number(plan.is_child) === 1) continue
    if (shouldShowBookingPlanOnPriceGrid(plan.booking_id, booking)) {
      visibleParentIds.add(String(plan.booking_id))
    }
  }

  return pricing.filter(plan => {
    if (!plan || plan.deleted) return false
    if (Number(plan.is_child) === 1) {
      return visibleParentIds.has(String(plan.id_parent))
    }
    return visibleParentIds.has(String(plan.booking_id))
  })
}

/** Lista prowizji per plan w FeeForm — zależna od globalnej prowizji. */
export function filterBookingRatesForFeeForm<T>(
  rates: Record<string, T> | null | undefined,
  bookingObjects: IItemData[] | null | undefined,
  booking?: { active?: unknown; rates?: Record<string, unknown> } | undefined,
): Record<string, T | number> {
  const ratesObj =
    rates && typeof rates === 'object' && !Array.isArray(rates) ? rates : {}
  const globalActive = Boolean(Number(booking && booking.active))

  if (globalActive) {
    const active = filterBookingRatesToActivePlans(rates, bookingObjects)
    const out: Record<string, T> = {}
    for (const [id, value] of Object.entries(active)) {
      if (isBookingRatePendingCommissionPlaceholder(value)) {
        continue
      }
      out[id] = value
    }
    return out
  }

  // Globalna wyłączona — wszystkie niezależne plany Booking (w tym nowe bez wpisu w rates).
  const out: Record<string, T | number> = {}
  const seen = new Set<string>()

  for (const obj of bookingObjects || []) {
    for (const room of obj.rooms || []) {
      if (!isActiveBookingIntegrationRoom(room)) continue
      const pricing = room && room.pricing
      if (!Array.isArray(pricing)) continue
      for (const p of pricing as IBookingRoomPricingPlan[]) {
        if (!p || p.deleted) continue
        if (isBookingDependentPlan(p)) continue
        const bid = p.booking_id
        if (bid === undefined || bid === null || bid === '') continue
        const id = String(bid)
        if (seen.has(id)) continue
        seen.add(id)

        if (Object.prototype.hasOwnProperty.call(ratesObj, id)) {
          out[id] = ratesObj[id] as T
        } else {
          out[id] = 0
        }
      }
    }
  }

  return out
}
